import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import './flow-diagram.css'

// FlowDiagram — the one-glance answer to "how does this all connect?", reachable
// from any page header. It renders the spine of the verse as a compact popover: a
// spring feeds, a signal asks, a star is held, a mission acts. Each node deep-links
// to its surface; the footer points at the full /map for the whole river.
//
// The trigger is deliberately NOT a "?" — the header already carries a HelpHint "?"
// for the term gloss, and two identical "?"s side by side read as noise. This is a
// distinct "how it connects ⟶" affordance so the two never collide (Springs round 2).
//
// The panel renders through a PORTAL to document.body, positioned against the
// trigger's measured rect and clamped to the viewport — same approach as ironvale's
// Tooltip / AppearancePanel — so it can never be clipped by an overflow ancestor or
// stack behind the left nav (the founder-reported "popover under the sidebar" bug).

interface FlowNode {
  glyph: string
  name: string
  to: string
  line: string
}

// The four beats of commitment, in order. Copy is flow-specific (one clause each)
// rather than the full vocabulary gloss — a diagram wants verbs, not paragraphs.
const NODES: FlowNode[] = [
  { glyph: '●', name: 'Springs', to: '/pipelines', line: 'Sources feed the lake on a schedule.' },
  { glyph: '◈', name: 'Signals', to: '/signals', line: 'Arrivals that cross your rules ask for judgment.' },
  { glyph: '✳', name: 'Constellation', to: '/graph', line: 'What you track becomes a star you hold.' },
  { glyph: '◉', name: 'Missions', to: '/missions', line: 'Stars power work with steps, gates, outcomes.' },
]

export interface FlowDiagramProps {
  /** Accessible label for the trigger + the panel region. */
  label?: string
  className?: string
}

export function FlowDiagram({ label = 'How it connects', className }: FlowDiagramProps) {
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  // Dismiss on Escape and on any click outside. With the panel portalled out of
  // the trigger's subtree, "outside" means outside BOTH the trigger and the panel.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open])

  // Position the portalled panel against the trigger's live rect: below it,
  // flipped above when there's more room there, then clamped so it stays fully
  // on-screen. Runs before paint so it never flashes at 0,0, and re-runs on
  // scroll/resize while open.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    const place = () => {
      const trigger = triggerRef.current
      const panel = panelRef.current
      if (!trigger || !panel) return
      const r = trigger.getBoundingClientRect()
      const pw = panel.offsetWidth
      const ph = panel.offsetHeight
      const vw = window.innerWidth
      const vh = window.innerHeight
      const gap = 10
      const margin = 12

      let top = r.bottom + gap
      const roomBelow = vh - r.bottom - gap - margin
      const roomAbove = r.top - gap - margin
      if (roomBelow < ph && roomAbove > roomBelow) top = r.top - gap - ph
      top = Math.max(margin, Math.min(top, vh - ph - margin))

      // Right-align to the trigger (the affordance sits toward the right of a
      // header), then clamp inside the viewport margins.
      let left = r.right - pw
      left = Math.max(margin, Math.min(left, vw - pw - margin))

      setPos({ top, left })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])

  return (
    <span className={['dw-flow', className].filter(Boolean).join(' ')}>
      <button
        ref={triggerRef}
        type="button"
        className="dw-flow__trigger"
        aria-label={label}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        how it connects <span className="dw-flow__trigger-glyph" aria-hidden="true">⟶</span>
      </button>

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="region"
            aria-label={label}
            className="dw-flow__panel"
            style={{
              top: pos ? `${pos.top}px` : 0,
              left: pos ? `${pos.left}px` : 0,
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            <div className="dw-flow__title">{label}</div>
            <div className="dw-flow__track">
              {NODES.map((n, i) => (
                <div className="dw-flow__step" key={n.name}>
                  <Link to={n.to} className="dw-flow__node" onClick={() => setOpen(false)}>
                    <span className="dw-flow__glyph" aria-hidden="true">
                      <span className="dw-flow__ink">{n.glyph}</span>
                    </span>
                    <span className="dw-flow__name">{n.name}</span>
                    <span className="dw-flow__line">{n.line}</span>
                    <span className="dw-flow__go">go to {n.name.toLowerCase()} →</span>
                  </Link>
                  {i < NODES.length - 1 && (
                    <span className="dw-flow__arrow" aria-hidden="true">↓</span>
                  )}
                </div>
              ))}
            </div>
            <div className="dw-flow__foot">
              Nothing moves without a gate you hold.{' '}
              <Link to="/map" className="dw-link" onClick={() => setOpen(false)}>
                See the whole river →
              </Link>
            </div>
          </div>,
          document.body,
        )}
    </span>
  )
}
